import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { Row, Col, Input, Button, Card, Form } from 'antd';
import { ToastContainer, toast } from 'react-toastify';
import { KeyIcon, Bot } from 'lucide-react';
import axios from "../components/Axios";

function UserApiKey() {
  const storedUser = localStorage.getItem("user");
  const userId = storedUser ? JSON.parse(storedUser)._id : null;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [apiData, setApiData] = useState({
    keywordLimit: 5,
    chatgptApiKey: '',
    geminiApiKey: '',
    claudeApiKey: ''
  });

  // Fetch existing API data
  const fetchApiData = async () => {
    if (!userId) return;
    setFetchLoading(true);
    const toastId = toast.loading('Loading API credentials...');

    try {
      // First try localStorage
      const storedCredentials = localStorage.getItem('userApiCredentials');
      let hasLocalData = false;

      if (storedCredentials) {
        try {
          const parsedCredentials = JSON.parse(storedCredentials);
          if (parsedCredentials.chatgptApiKey || parsedCredentials.geminiApiKey || parsedCredentials.claudeApiKey) {
            setApiData({
              keywordLimit: parsedCredentials.keywordLimit || 5,
              chatgptApiKey: parsedCredentials.chatgptApiKey || '',
              geminiApiKey: parsedCredentials.geminiApiKey || '',
              claudeApiKey: parsedCredentials.claudeApiKey || ''
            });
            form.setFieldsValue({
              keywordLimit: parsedCredentials.keywordLimit || 5,
              chatgptApiKey: parsedCredentials.chatgptApiKey || '',
              geminiApiKey: parsedCredentials.geminiApiKey || '',
              claudeApiKey: parsedCredentials.claudeApiKey || ''
            });
            hasLocalData = true;
          }
        } catch (e) {
          console.error('Error parsing stored credentials:', e);
        }
      }

      const response = await axios.get(`/api/user-credentials/${userId}`);
      if (response.data && response.data.success) {
        const data = response.data.data;
        if (!hasLocalData) {
          setApiData({
            keywordLimit: data.keywordLimit || 5,
            chatgptApiKey: data.chatgptApiKey || '',
            geminiApiKey: data.geminiApiKey || '',
            claudeApiKey: data.claudeApiKey || ''
          });
          form.setFieldsValue({
            keywordLimit: data.keywordLimit || 5,
            chatgptApiKey: data.chatgptApiKey || '',
            geminiApiKey: data.geminiApiKey || '',
            claudeApiKey: data.claudeApiKey || ''
          });
        }
      }

      toast.update(toastId, {
        render: 'API credentials loaded successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
    } catch (error) {
      console.error('Error fetching API credentials:', error);
      toast.update(toastId, {
        render: 'Failed to load API credentials',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setFetchLoading(false);
    }
  };

  // Save API settings
  const handleSubmit = async (values) => {
    if (!userId) {
      toast.error('User not found. Please login again.');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post('/api/update-user-credentials', {
        userId,
        keywordLimit: values.keywordLimit,
        chatgptApiKey: values.chatgptApiKey || '',
        geminiApiKey: values.geminiApiKey || '',
        claudeApiKey: values.claudeApiKey || ''
      });

      if (response.data && response.data.success) {
        setApiData(values);
        const apiCredentials = {
          keywordLimit: values.keywordLimit,
          chatgptApiKey: values.chatgptApiKey || '',
          geminiApiKey: values.geminiApiKey || '',
          claudeApiKey: values.claudeApiKey || '',
          lastUpdated: new Date().toISOString()
        };
        localStorage.setItem('userApiCredentials', JSON.stringify(apiCredentials));
        toast.success('API credentials saved successfully!');
      } else {
        toast.error(response.data.message || 'Failed to save API credentials');
      }
    } catch (error) {
      console.error('Error saving API credentials:', error);
      toast.error(error.response?.data?.error || 'An error occurred while saving API credentials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiData();
  }, [userId]);

  return (
    <>
      {/* <ToastContainer
        position="top-right"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      /> */}

      <div style={{ backgroundColor: '#f9fafb' }}>
        <Header />
        <div style={{ display: 'flex' }}>
          <Sidebar />
          <main style={{ flex: 1, padding: '30px' }}>
            <div className="subMainContainer">
              <div className="headerCommom">
                <div className='heading'>
                  User API Key Settings
                </div>
              </div>

              <Card style={{ marginTop: '24px' }}>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  initialValues={{
                    keywordLimit: apiData.keywordLimit,
                    chatgptApiKey: apiData.chatgptApiKey,
                    geminiApiKey: apiData.geminiApiKey,
                    claudeApiKey: apiData.claudeApiKey
                  }}
                >
                  {/* AI API Keys Section */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <Bot size={18} />
                      <span style={{ fontSize: '16px', fontWeight: '600' }}>AI API Keys</span>
                      <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 400 }}>
                        — Add the keys for the AI models you want to use for answer generation
                      </span>
                    </div>

                    <Row gutter={24}>
                      {/* OpenAI */}
                      <Col xs={24} md={8}>
                        <Form.Item
                          label={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>🤖</span>
                              <span>OpenAI (ChatGPT) API Key</span>
                            </div>
                          }
                          name="chatgptApiKey"
                        >
                          <Input.Password
                            size="large"
                            placeholder="sk-..."
                            prefix={<Bot size={16} style={{ color: '#6b7280' }} />}
                          />
                        </Form.Item>
                      </Col>

                      {/* Gemini */}
                      <Col xs={24} md={8}>
                        <Form.Item
                          label={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>✨</span>
                              <span>Google Gemini API Key</span>
                            </div>
                          }
                          name="geminiApiKey"
                        >
                          <Input.Password
                            size="large"
                            placeholder="AIza..."
                            prefix={<Bot size={16} style={{ color: '#6b7280' }} />}
                          />
                        </Form.Item>
                      </Col>

                      {/* Claude */}
                      <Col xs={24} md={8}>
                        <Form.Item
                          label={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>🧠</span>
                              <span>Anthropic Claude API Key</span>
                            </div>
                          }
                          name="claudeApiKey"
                        >
                          <Input.Password
                            size="large"
                            placeholder="sk-ant-..."
                            prefix={<Bot size={16} style={{ color: '#6b7280' }} />}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>

                  {/* Save Button */}
                  <Row>
                    <Col>
                      <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        loading={loading}
                        icon={<KeyIcon size={16} />}
                      >
                        Save Settings
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card>

              {/* Instructions */}
              <Card
                title="How to get your API keys"
                style={{ marginTop: '24px' }}
                type="inner"
              >
                <div style={{ lineHeight: '1.8' }}>
                  <p>
                    <strong>🤖 OpenAI API Key:</strong>{' '}
                    <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com/api-keys</a>
                    {' '}— Used for GPT-4o, GPT-4 Turbo and other ChatGPT models.
                  </p>
                  <p>
                    <strong>✨ Google Gemini API Key:</strong>{' '}
                    <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">aistudio.google.com/app/apikey</a>
                    {' '}— Free tier available. Used for Gemini 1.5 Pro and Flash.
                  </p>
                  <p>
                    <strong>🧠 Anthropic Claude API Key:</strong>{' '}
                    <a href="https://console.anthropic.com/account/keys" target="_blank" rel="noopener noreferrer">console.anthropic.com/account/keys</a>
                    {' '}— Used for Claude 3.5 Sonnet and other Claude models.
                  </p>
                  <p style={{ marginTop: '12px', color: '#6b7280', fontSize: '13px' }}>
                    💡 You only need to add the API key for the model you want to use. You can add all three for full flexibility.
                  </p>
                </div>
              </Card>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

export default UserApiKey;